import type {
  AddWorkspaceUserRequest,
  AddWorkspaceUserResponse,
  EmailTemplateDetail,
  EmailTemplateListItem,
  EmailTemplatePreviewResult,
  EmailTemplateValidationError,
  GetWorkspaceUsersParams,
  WorkspaceMembershipDto,
  WorkspaceUsersResponse,
} from '../types/workspace.types';

import { oidcUserManagerSettings } from '@config/authConfig';
import { getAccessCredential } from 'src/auth/utils/inMemoryTokenStore';

function getAuthority(): string {
  return (oidcUserManagerSettings.authority ?? '').replace(/\/$/, '');
}

interface RawMembership {
  membershipId?: string;
  MembershipId?: string;
  tenantId?: string;
  TenantId?: string;
  tenantName?: string;
  TenantName?: string;
  contextType?: string;
  ContextType?: string;
  companyId?: string;
  CompanyId?: string;
  companyName?: string;
  CompanyName?: string;
  applicationRole?: string;
  ApplicationRole?: string;
  workspaceRoles?: string[];
  WorkspaceRoles?: string[];
  isActive?: boolean;
  IsActive?: boolean;
}

export async function fetchSettingsMemberships(
  signal?: AbortSignal
): Promise<WorkspaceMembershipDto[]> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const response = await fetch(`${getAuthority()}/api/v1/my-memberships`, {
    headers: { Authorization: `Bearer ${cred.value}` },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch memberships (${response.status})`);
  }

  const body = await response.json();
  const raw: RawMembership[] = Array.isArray(body)
    ? body
    : body.memberships ?? body.Memberships ?? [];

  return raw.map(
    (m): WorkspaceMembershipDto => ({
      membershipId: m.membershipId ?? m.MembershipId ?? '',
      tenantId: m.tenantId ?? m.TenantId ?? '',
      tenantName: m.tenantName ?? m.TenantName ?? '',
      contextType: (m.contextType ?? m.ContextType ?? 'company') as
        | 'tenant'
        | 'company',
      companyId: m.companyId ?? m.CompanyId,
      companyName: m.companyName ?? m.CompanyName,
      applicationRole: m.applicationRole ?? m.ApplicationRole,
      workspaceRoles: m.workspaceRoles ?? m.WorkspaceRoles ?? [],
      isActive: m.isActive ?? m.IsActive ?? false,
    })
  );
}

export async function getWorkspaceUsers(
  params: GetWorkspaceUsersParams,
  signal?: AbortSignal
): Promise<WorkspaceUsersResponse> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const url = new URL(`${getAuthority()}/api/v1/workspaces/users`);
  url.searchParams.set('page', String(params.page));
  url.searchParams.set('pageSize', String(params.pageSize));
  if (params.email) url.searchParams.set('email', params.email);

  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${cred.value}` },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch workspace users (${response.status})`);
  }

  return response.json() as Promise<WorkspaceUsersResponse>;
}

export async function addWorkspaceUser(
  request: AddWorkspaceUserRequest,
  signal?: AbortSignal
): Promise<{ status: number; data: AddWorkspaceUserResponse }> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const response = await fetch(`${getAuthority()}/api/v1/workspaces/users`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${cred.value}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (response.status === 201 || response.status === 202) {
    const data = (await response.json()) as AddWorkspaceUserResponse;
    return { status: response.status, data };
  }

  if (response.status === 409) {
    const body = await response.json().catch(() => ({}));
    const code = (body as { error?: string }).error ?? 'CONFLICT';
    throw Object.assign(new Error(code), { status: 409, code });
  }

  if (response.status === 403) {
    throw Object.assign(new Error('FORBIDDEN'), { status: 403 });
  }

  if (response.status === 400) {
    const body = await response.json().catch(() => ({}));
    const detail = (body as { error?: string }).error ?? 'BAD_REQUEST';
    throw Object.assign(new Error(detail), { status: 400 });
  }

  throw Object.assign(new Error(`Unexpected error (${response.status})`), {
    status: response.status,
  });
}

// --- Update Member Role  ---

export interface UpdateMemberRoleResponse {
  userId: string;
  applicationRole: string;
}

export async function updateMemberRole(
  userId: string,
  applicationRole: string,
  signal?: AbortSignal
): Promise<UpdateMemberRoleResponse> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const response = await fetch(
    `${getAuthority()}/api/v1/workspaces/members/${userId}/role`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${cred.value}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ applicationRole }),
      signal,
    }
  );

  if (response.ok) {
    return response.json() as Promise<UpdateMemberRoleResponse>;
  }

  const body = await response.json().catch(() => ({}));
  const code = (body as { error?: string }).error;

  throw Object.assign(
    new Error(code ?? `Unexpected error (${response.status})`),
    { status: response.status, code }
  );
}

// --- Remove Member  ---

export interface RemoveMemberResponse {
  userId: string;
  outcome: string;
}

export async function removeMember(
  userId: string,
  signal?: AbortSignal
): Promise<RemoveMemberResponse> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const response = await fetch(
    `${getAuthority()}/api/v1/workspaces/members/${userId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${cred.value}` },
      signal,
    }
  );

  if (response.ok) {
    return response.json() as Promise<RemoveMemberResponse>;
  }

  const body = await response.json().catch(() => ({}));
  const code = (body as { error?: string }).error;

  throw Object.assign(
    new Error(code ?? `Unexpected error (${response.status})`),
    { status: response.status, code }
  );
}

// --- Workspace Email Templates (spec 003-05) ---

function templateTypeToSlug(type: string): string {
  switch (type) {
    case 'EmailVerification':
      return 'email-verification';
    case 'TwoFactor':
      return 'two-factor';
    case 'PasswordReset':
      return 'password-reset';
    case 'Invitation':
      return 'invitation';
    default:
      return type;
  }
}

async function buildApiError(response: Response): Promise<Error> {
  const body = await response.json().catch(() => ({}));

  const firstValidationMessage =
    ((body as { errors?: EmailTemplateValidationError }).errors?.body ??
      [])[0] ?? null;

  const fallbackMessage =
    (body as { error?: string; detail?: string }).error ??
    (body as { error?: string; detail?: string }).detail ??
    `Request failed (${response.status})`;

  const err = new Error(firstValidationMessage ?? fallbackMessage) as Error & {
    status?: number;
    body?: unknown;
  };
  err.status = response.status;
  err.body = body;
  return err;
}

export async function listWorkspaceEmailTemplates(
  tenantId: string,
  signal?: AbortSignal
): Promise<EmailTemplateListItem[]> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const response = await fetch(
    `${getAuthority()}/api/v1/tenants/${tenantId}/email-templates`,
    {
      headers: { Authorization: `Bearer ${cred.value}` },
      signal,
    }
  );

  if (!response.ok) {
    throw await buildApiError(response);
  }

  return response.json() as Promise<EmailTemplateListItem[]>;
}

export async function getWorkspaceEmailTemplate(
  tenantId: string,
  templateType: string,
  signal?: AbortSignal
): Promise<EmailTemplateDetail> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const slug = templateTypeToSlug(templateType);
  const response = await fetch(
    `${getAuthority()}/api/v1/tenants/${tenantId}/email-templates/${slug}`,
    {
      headers: { Authorization: `Bearer ${cred.value}` },
      signal,
    }
  );

  if (!response.ok) {
    throw await buildApiError(response);
  }

  return response.json() as Promise<EmailTemplateDetail>;
}

export async function upsertWorkspaceEmailTemplate(
  tenantId: string,
  templateType: string,
  body: string,
  signal?: AbortSignal
): Promise<EmailTemplateDetail> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const slug = templateTypeToSlug(templateType);
  const response = await fetch(
    `${getAuthority()}/api/v1/tenants/${tenantId}/email-templates/${slug}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${cred.value}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ body }),
      signal,
    }
  );

  if (!response.ok) {
    throw await buildApiError(response);
  }

  return response.json() as Promise<EmailTemplateDetail>;
}

export async function deleteWorkspaceEmailTemplate(
  tenantId: string,
  templateType: string,
  signal?: AbortSignal
): Promise<void> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const slug = templateTypeToSlug(templateType);
  const response = await fetch(
    `${getAuthority()}/api/v1/tenants/${tenantId}/email-templates/${slug}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${cred.value}`,
      },
      signal,
    }
  );

  if (!response.ok) {
    throw await buildApiError(response);
  }
}

export async function previewWorkspaceEmailTemplate(
  templateType: string,
  body: string,
  signal?: AbortSignal
): Promise<EmailTemplatePreviewResult> {
  const cred = getAccessCredential();
  if (!cred) throw new Error('No access credential available');

  const response = await fetch(
    `${getAuthority()}/api/v1/email-templates/preview`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${cred.value}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ templateType, body }),
      signal,
    }
  );

  if (!response.ok) {
    throw await buildApiError(response);
  }

  return response.json() as Promise<EmailTemplatePreviewResult>;
}
